import { useTranslation } from "react-i18next";
import type { PeriodSummaryMode } from "@/pages/human-resources/time-management/business/analysis/PeriodSummaryMode.ts";
import { DateStyle, getLastYearSameDay } from "@/shared/utils/Dates.ts";
import type { PeriodSummaryRange } from "@/pages/human-resources/time-management/business/analysis/PeriodSummaryRange.ts";
import useDateFormatter from "@/shared/custom-hook/useDateFormatter.ts";
import { LEGACY_TIME_ZONE } from "@/shared/utils/LegacyDates.ts";
import { DateTime } from "luxon";

export function usePeriodSummaryDescription(
  value: PeriodSummaryMode,
  date: Date,
  range: PeriodSummaryRange,
): string {
  const { t } = useTranslation("humanResources", {
    keyPrefix: "timeManagement.analysis.modeDescription",
  });
  const { formatDate } = useDateFormatter(LEGACY_TIME_ZONE);

  switch (range) {
    case "DAY": {
      return dayDescription();
    }
    case "MONTH": {
      return monthDescription();
    }
    case "WEEK": {
      return ""; // Not available yet
    }
  }

  function dayDescription(): string {
    switch (value) {
      case "PREVIOUS_YEAR":
        return t("dayRange.previousYear", {
          date: formatDate(
            getLastYearSameDay(date),
            DateStyle.WEEKDAY_DATE_MONTH_YEAR,
          ),
        });
      case "TREND":
        return t("dayRange.trend", {
          weekdayPlural: `${formatDate(date, DateStyle.WEEKDAY)}s`,
        });
      default:
        return "";
    }
  }

  function monthDescription(): string {
    switch (value) {
      case "PREVIOUS_YEAR":
        return t("monthRange.previousYear", {
          date: formatDate(
            DateTime.fromJSDate(date).minus({ year: 1 }).toJSDate(),
            DateStyle.YEAR_MONTH_WORD,
          ),
        });
      case "TREND":
        return t("monthRange.trend");
      default:
        return "";
    }
  }
}
